import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { PhysicalOrdersService } from "@/services/physicalOrders.service";
import useModalStore from "@/store/useModalStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Order } from "../hooks/useOrders";
import { ShipmentSelector } from "./ShipmentSelector";

export const DELIVERY_FAILED_MODAL_NAME = "delivery-failed-modal";

const deliveryFailedSchema = z.object({
  failure_reason: z.string().min(1, "required").max(500, "max_length"),
  tracking_id: z.string().optional(),
});

type DeliveryFailedFormData = z.infer<typeof deliveryFailedSchema>;

interface DeliveryFailedModalProps {
  order: Order | null;
  onSuccess?: () => void;
}

export function DeliveryFailedModal({ order, onSuccess }: DeliveryFailedModalProps) {
  const { t } = useTranslation(["common", "orders"]);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const isOpen = modals[DELIVERY_FAILED_MODAL_NAME] || false;
  const requiresTrackingId = Boolean(order?.has_multiple_trackings);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<DeliveryFailedFormData>({
    resolver: zodResolver(deliveryFailedSchema),
    mode: "onChange",
    defaultValues: {
      failure_reason: "",
      tracking_id: "",
    },
  });

  const trackingId = watch("tracking_id");

  useEffect(() => {
    if (isOpen) {
      reset({ failure_reason: "", tracking_id: "" });
    }
  }, [isOpen, reset]);

  const { mutate: markDeliveryFailed, isPending } = useMutation({
    mutationFn: async (data: DeliveryFailedFormData) => {
      if (!order) throw new Error("Order not found");
      if (requiresTrackingId && !data.tracking_id) {
        throw new Error(t("common:required_field"));
      }

      return PhysicalOrdersService.markDeliveryFailed(order.id.replace("#", ""), {
        failure_reason: data.failure_reason,
        tracking_id: data.tracking_id || undefined,
      });
    },
    onSuccess: () => {
      toast({
        description: t("orders:delivery_failed_modal.success"),
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["physical-orders"] });
      queryClient.invalidateQueries({ queryKey: ["physical-orders-summary"] });
      queryClient.invalidateQueries({ queryKey: ["getSellInfo"] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      toast({
        description: error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    reset({ failure_reason: "", tracking_id: "" });
    closeModal(DELIVERY_FAILED_MODAL_NAME);
  };

  const onSubmit = (data: DeliveryFailedFormData) => {
    markDeliveryFailed(data);
  };

  const canSubmit = isValid && (!requiresTrackingId || Boolean(trackingId));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("orders:delivery_failed_modal.title")}</DialogTitle>
          <DialogDescription>
            {t("orders:delivery_failed_modal.description", { orderId: order?.id })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 py-4">
            <ShipmentSelector
              trackings={order?.delivery_trackings || []}
              value={trackingId}
              onChange={(value) => setValue("tracking_id", value, { shouldValidate: true })}
              error={requiresTrackingId && !trackingId}
            />
            <div className="grid gap-2">
              <Label htmlFor="failure_reason">
                {t("orders:delivery_failed_modal.failure_reason")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="failure_reason"
                {...register("failure_reason")}
                placeholder={t("orders:delivery_failed_modal.failure_reason_placeholder")}
                className={errors.failure_reason ? "border-destructive" : ""}
                rows={4}
              />
              {errors.failure_reason && (
                <span className="text-xs text-destructive">
                  {errors.failure_reason.message === "max_length"
                    ? t("orders:delivery_failed_modal.max_length_error")
                    : t("common:required_field")}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {t("orders:delivery_failed_modal.reason_hint")}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={!canSubmit || isPending}>
              {isPending ? t("common:saving") : t("orders:delivery_failed_modal.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
